import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mapResponse } from '@ngrx/operators';
import { catchError, exhaustMap, from, map, of, tap } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AuthApi } from '../api/auth.api';
import { TokenStorage } from '../session/token-storage';
import { AuthActions } from './auth.actions';

/** Maps Firebase auth error codes to user-facing copy. */
function toMessage(error: unknown): string {
  const code = (error as { code?: string })?.code ?? '';
  const message = (error as { message?: string })?.message ?? '';

  // A blocking-function rejection (wrong domain / not in AdminInvites) surfaces
  // to the client as auth/internal-error wrapping a PERMISSION_DENIED payload.
  if (message.includes('PERMISSION_DENIED') || message.includes('not authorized')) {
    return "This account isn't authorized for the NexTech IDP Admin Console. Contact an administrator.";
  }

  switch (code) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'Sign-in was cancelled.';
    case 'auth/popup-blocked':
      return 'Your browser blocked the sign-in popup. Allow popups and try again.';
    case 'auth/invalid-email':
      return 'That email address is not valid.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Incorrect email or password.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    default:
      return 'Unable to sign in. Please try again.';
  }
}

@Injectable()
export class AuthEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(AuthApi);
  private readonly storage = inject(TokenStorage);
  private readonly router = inject(Router);

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      exhaustMap(({ email, password }) => {
        // Explicit logins always target the CONFIGURED tenant; the persisted
        // tenant id is only for Restore Session. (A stale persisted id from
        // another environment on this origin — e.g. dev's tenant after
        // switching a local serve to qa — would otherwise fail every login.)
        const tenantId = environment.firebaseTenantId;
        this.api.setTenant(tenantId);

        return from(this.api.signIn(email, password)).pipe(
          mapResponse({
            next: (user) => AuthActions.loginSuccess({ user, tenantId }),
            error: (error) => AuthActions.loginFailure({ error: toMessage(error) }),
          }),
        );
      }),
    ),
  );

  // Google Workspace sign-in — the only login path exposed in the UI. Same
  // success/failure shape as login$, so it reuses loginSuccess/loginFailure and
  // the navigate-on-success effect below. The blocking function enforces the
  // domain + AdminInvites gate and surfaces rejections through toMessage.
  googleLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.googleLogin),
      exhaustMap(() => {
        // Configured tenant only — see login$ for why the persisted id is
        // not consulted on explicit logins.
        const tenantId = environment.firebaseTenantId;
        this.api.setTenant(tenantId);

        return from(this.api.signInWithGoogle()).pipe(
          mapResponse({
            next: (user) => AuthActions.loginSuccess({ user, tenantId }),
            error: (error) => AuthActions.loginFailure({ error: toMessage(error) }),
          }),
        );
      }),
    ),
  );

  // Persist tenant + navigate to main only on successful login.
  loginSuccessNavigate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        tap(({ tenantId }) => {
          this.storage.setTenantId(tenantId);
          void this.router.navigateByUrl('/');
        }),
      ),
    { dispatch: false },
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      exhaustMap(() =>
        from(this.api.signOut()).pipe(
          map(() => AuthActions.logoutSuccess()),
          // Even if the SDK sign-out fails, surface a clean local logout.
          catchError(() => of(AuthActions.logoutSuccess())),
        ),
      ),
    ),
  );

  logoutSuccessNavigate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logoutSuccess),
        tap(() => {
          this.storage.clear();
          void this.router.navigateByUrl('/auth');
        }),
      ),
    { dispatch: false },
  );

  // Rehydrate from the persisted Identity Platform session on startup.
  restoreSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.restoreSession),
      exhaustMap(() => {
        const tenantId = this.storage.getTenantId();
        if (tenantId) {
          this.api.setTenant(tenantId);
        }
        return from(this.api.resolveCurrentUser()).pipe(
          map((user) =>
            AuthActions.restoreSessionSuccess({ user, tenantId: user ? tenantId : null }),
          ),
          catchError(() =>
            of(AuthActions.restoreSessionSuccess({ user: null, tenantId: null })),
          ),
        );
      }),
    ),
  );
}
